import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Box,
    Alert,
    InputAdornment,
    IconButton,
    Avatar,
    CircularProgress,
} from '@mui/material';
import { Lock, LockReset, Visibility, VisibilityOff } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 8) {
            setError('Password must be at least 8 characters');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        try {
            await api.post('/auth/reset-password', { token, password });
            setSuccess(true);
            toast.success('Password updated -- you can sign in now');
            setTimeout(() => navigate('/login'), 2500);
        } catch (err) {
            const message = err.response?.data?.message || 'Could not reset the password';
            setError(message);
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <Box
                sx={{
                    marginTop: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        padding: 4,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    <Avatar
                        sx={{
                            m: 1,
                            bgcolor: 'primary.main',
                            width: 56,
                            height: 56,
                        }}
                    >
                        <LockReset fontSize="large" />
                    </Avatar>

                    <Typography component="h1" variant="h5" gutterBottom>
                        Set a New Password
                    </Typography>

                    {!token && (
                        <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
                            This reset link is missing its token. Request a new one from the
                            "Forgot password?" page.
                        </Alert>
                    )}

                    {token && error && (
                        <Alert severity="error" sx={{ width: '100%', mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {success && (
                        <Alert severity="success" sx={{ width: '100%', mt: 2 }}>
                            Password updated! Redirecting to sign in...
                        </Alert>
                    )}

                    {token && (
                        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label="New Password"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                autoFocus
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                disabled={success || loading}
                                helperText="At least 8 characters"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock color="action" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => setShowPassword(!showPassword)}
                                                edge="end"
                                                disabled={success || loading}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                label="Confirm New Password"
                                type={showPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={success || loading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Lock color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2, height: 48 }}
                                disabled={loading || success}
                                size="large"
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Update Password'}
                            </Button>
                        </Box>
                    )}

                    <Box sx={{ textAlign: 'center', mt: token ? 0 : 2 }}>
                        <Link to="/login" style={{ textDecoration: 'none' }}>
                            <Typography variant="body2" color="primary">
                                Back to Login
                            </Typography>
                        </Link>
                    </Box>
                </Paper>

                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                    © {new Date().getFullYear()} Solidpro Mech. All rights reserved.
                </Typography>
            </Box>
        </Container>
    );
};

export default ResetPassword;
